/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Chaitanya
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { formatISO, addDays, endOfDay, startOfDay } from 'date-fns';

import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import SociollaText from '@components/sociolla/SociollaText';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import StatusBadge from '@components/global/StatusBadge';
import ReminderNotice from '@components/global/ReminderNotice';
import Image from '@components/global/Image';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import SelectBox from '@components/form/SelectBox';
import ModalFull from '@components/modal/ModalFull';
import Button from '@components/form/Button';

import { isWeb, fullWidth, isMobileWeb, isAndroid } from '@global/screens';
import {
  parseDate,
  currency,
  exportCsvApi,
  formatSkpType,
  firstLetter,
  deSlug,
  getSkpJoinedReminderDays,
} from '@global/helpers';
import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { MsB2b } from '@api/ms-b2b';
import { filterAdded, paramsReseted } from '@/store/params';
import { showAlert } from '@/store/global';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetSkpExternalListQuery,
  useGetSkpExternalCountQuery,
  useGetSkpExternalInfiniteListInfiniteQuery,
} from '@api/listApi';

const dimension = Dimensions.get('screen');

const SKP_TYPE = [
  { value: 'mt', name: 'MT' },
  { value: 'gt', name: 'GT' },
  { value: 'subdistributor', name: 'Sub Distributor' },
];

const SKP_TYPE_EXPORT = [
  { value: 'all', name: 'All' },
  { value: 'mt', name: 'MT' },
  { value: 'gt', name: 'GT' },
  { value: 'subdistributor', name: 'Sub Distributor' },
];

const EXPORT_ROLES = [
  'sales_admin',
  'salesman',
  'sales_supervisor',
  'branch_head',
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'superadmin',
  'finance_supervisor',
  'finance_head',
  'tax_support',
  'distributor_manager',
  'billing_supervisor',
  'general_manager_marketing',
  'general_manager',
  'budget_controller',
  'marketing_admin',
  'marketing_support',
  'general',
  'regional_distributor_manager',
];

const tableStructure = {
  skp_reference: {
    head: 'SKP reference',
    body: 'skp_internal.reference_code',
  },
  reference_code: {
    head: 'SKP number',
    body: 'reference_code',
    type: 'slot',
  },
  cost_type: {
    head: 'COA Activity Promo',
    body: 'skp_internal.coa_tax.cost_type',
  },
  skp_type: {
    head: 'Type',
    body: 'skp_type',
  },
  period: {
    head: 'Periode',
    body: 'period',
  },
  sales_team: {
    head: 'Sales Team',
    body: 'sales_team.name',
  },
  customer: {
    head: 'Customer Name',
    body: 'customer.name',
  },
  outlet: {
    head: 'Outlet Name',
    body: 'outlet.name',
  },
  claim: {
    head: 'Claim',
    body: 'claim_type',
    type: 'status',
  },
  skp_balance: {
    head: 'SKP Balance Remaining',
    body: 'skp_balance',
    type: 'currency',
  },
  total_biaya: {
    head: 'Total Biaya/DPP(coa exp)',
    body: 'total_biaya',
    type: 'currency',
  },
  pph: {
    head: 'PPH(coa wht)',
    body: 'pph',
    type: 'currency',
  },
  ppn: {
    head: 'PPN(coa wht)',
    body: 'ppn',
    type: 'currency',
  },
  total_nett: {
    head: 'Total Nett(coa ar)',
    body: 'total_nett',
    type: 'currency',
  },
  status: {
    head: 'Status',
    body: 'status',
    type: 'slot',
  },
  last_updated_by: {
    head: 'Last Updated, By',
    body: 'last_updated_by',
  },
};

// UI filter shape → API query filter (was the inline transform in the old finalParam):
//  - `keyword` unwrapped from the search box's { $regex } object
//  - `need_approval_role_<role>` status → status:need_approval + current_approval
//  - `sales_team` / `coa_taxes` (arrays) → sales_team.id / skp_internal.coa_tax.id: { $in }
//  - `skp_type` lowercased
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  const kw = rawFilter.keyword?.$regex ?? rawFilter.keyword;
  if (kw) {
    filter.keyword = kw;
  }
  if (rawFilter.from_date) {
    filter.from_date = rawFilter.from_date;
  }
  if (rawFilter.to_date) {
    filter.to_date = rawFilter.to_date;
  }
  if (rawFilter.status) {
    filter.status = rawFilter.status;
    if (
      typeof filter.status === 'string' &&
      filter.status.includes('need_approval_role')
    ) {
      const role = filter.status.split('need_approval_role_')?.[1];
      if (role) {
        filter.current_approval = role;
      }
      filter.status = 'need_approval';
    }
  }
  if (rawFilter.sales_team?.length) {
    filter['sales_team.id'] = {
      $in: rawFilter.sales_team.map((el) => el.id),
    };
  }
  if (rawFilter.coa_taxes?.length) {
    filter['skp_internal.coa_tax.id'] = {
      $in: rawFilter.coa_taxes.map((el) => el.id),
    };
  }
  if (rawFilter.skp_type) {
    filter.skp_type = rawFilter.skp_type?.toLowerCase
      ? rawFilter.skp_type.toLowerCase()
      : rawFilter.skp_type;
  }
  return filter;
};

// Scope the sales-team search to the user's own teams (empty = show all).
const buildSalesTeamFilter = (userData) => {
  if (userData?.sales_team?.length) {
    return {
      _id: { $in: userData.sales_team.map((team) => team.id) },
    };
  } else {
    return {};
  }
};

// Display-formatted row. RTK cache objects are frozen → build a new object.
const mapRow = (item) => {
  const next = { ...item };
  if (item._id) {
    next.id = item._id;
  }
  if (item.from_date && item.to_date) {
    next.period = `${parseDate(item.from_date)} - ${parseDate(item.to_date)}`;
  }
  const lastUpdatedAt = item.updated_at ? parseDate(item.updated_at) : '-';
  const updatedBy = item.updated_by?.name || '-';
  next.last_updated_by = `${lastUpdatedAt}, ${updatedBy}`;
  next.skp_type = formatSkpType(item.skp_internal?.skp_type);
  next.joined_reminder =
    getSkpJoinedReminderDays(item) != null ? 'SKP action required' : null;
  return next;
};

// Status options incl. Pending-Approval-by-role from the SKP matrix approval.
// Fetched once — reference data, not per search.
const fetchMatrixStatuses = async () => {
  const tempStatuses = [
    { name: 'Approved', value: 'approved' },
    { name: 'Rejected', value: 'rejected' },
    { name: 'Draft', value: 'draft' },
    { name: 'Pending Approval', value: 'need_approval' },
  ];
  try {
    const matrix = await MsB2b.getMatrixApproval({
      params: { filter: { applicable_for: 'skp_approval' } },
    });
    if (matrix?.data?.data?.length) {
      const matrixSkp = matrix.data.data[0];
      const matrixSkpExternal = (matrixSkp?.rule || []).filter((r) =>
        r.applicable_on.includes('skp_external'),
      );
      const approvers = [];
      for (const rule of matrixSkpExternal) {
        const ruleApprovers = (rule?.approver || []).map((a) => a.role);
        approvers.push(...ruleApprovers);
      }
      const uniqueApprovers = [...new Set(approvers)].map((a) => ({
        name: `Pending Approval By ${firstLetter(deSlug(a))}`,
        value: `need_approval_role_${a}`,
      }));
      tempStatuses.push(...uniqueApprovers);
    }
  } catch (e) {}
  tempStatuses.push(
    { name: 'Joined', value: 'joined' },
    { name: 'Cancelled', value: 'cancelled' },
    { name: 'Completed', value: 'completed' },
  );
  return tempStatuses;
};

const SkpExternalScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.auth.user);
  const showSkpExternalExport = useSelector(
    (state) => state.modal.showSkpExternalExport,
  );
  const showFilter = useSelector((state) => state.modal.showFilter);

  // ── local state ──
  const [coaTaxes, setCoaTaxes] = useState([]);
  const [coaTaxesLoading, setCoaTaxesLoading] = useState(false);
  const [coaTaxesParams, setCoaTaxesParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });
  const [exporting, setExporting] = useState(false);
  const [filterExport, setFilterExport] = useState({
    from_date: '',
    to_date: '',
    coa_taxes: '',
    sales_team: '',
    status: '',
  });
  const [statuses, setStatuses] = useState([]);
  const [draftFilter, setDraftFilter] = useState({});
  const [draftSalesTeam, setDraftSalesTeam] = useState('');
  const [draftCoaTaxes, setDraftCoaTaxes] = useState('');

  const getCoaTaxSettings = useApi(MsOrder.getCoaTaxSettings);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetSkpExternalListQuery,
    countHook: useGetSkpExternalCountQuery,
    infiniteHook: useGetSkpExternalInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const showExportButton = useMemo(
    () => EXPORT_ROLES.includes(userData?.role?.code),
    [userData],
  );

  // Current search text, derived from the applied keyword filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // Display-formatted rows. RTK cache objects are frozen → build new objects.
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── effects ──
  useEffect(() => {
    let mounted = true;
    fetchMatrixStatuses().then((tempStatuses) => {
      if (mounted) {
        setStatuses(tempStatuses);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Seed the staged filter draft from the applied filter whenever the modal opens.
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftSalesTeam(applied.sales_team || '');
      setDraftCoaTaxes(applied.coa_taxes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  // ── handlers ──
  const draftDateHandler = (value, type) => {
    const date = new Date(value);
    setDraftFilter((f) => {
      const next = { ...f };
      if (type === 'from_date') {
        const startDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        );
        next.from_date = { $gte: startDate.toISOString() };
      } else {
        const endDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          23,
          59,
          59,
        );
        next.to_date = { $lte: endDate.toISOString() };
      }
      return next;
    });
  };

  const goToDetail = (data) => {
    openChild('SkpExternalDetailScreen', {
      titleScreen: data.reference_code ? data.reference_code : '-',
      name: data.customer && data.customer.name ? data.customer.name : '-',
      id: data._id,
      go_back_key: props.route.key,
    });
  };

  const onExport = async () => {
    setExporting(true);
    try {
      if (!filterExport.from_date || !filterExport.to_date) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.coa_taxes?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter COA taxes must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.sales_team?.length) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter sales team must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.skp_type) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Type status must be filled',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      const payload = {
        from_date: { $gte: filterExport.from_date },
        to_date: { $lte: filterExport.to_date },
        'skp_internal.coa_tax.id': {
          $in: filterExport.coa_taxes.map((coa) => coa.id),
        },
        'sales_team.id': { $in: filterExport.sales_team.map((st) => st.id) },
      };
      if (filterExport.skp_type.value != 'all') {
        payload['skp_type'] = filterExport.skp_type.value;
      }
      if (filterExport.status && filterExport.status !== 'all') {
        if (filterExport.status.startsWith('need_approval_role_')) {
          payload.status = 'need_approval';
          payload.current_approval = filterExport.status.replace(
            'need_approval_role_',
            '',
          );
        } else {
          payload.status = filterExport.status;
        }
      }
      await exportCsvApi('skp-externals', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });
      setExporting(false);
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
      setExporting(false);
    }
  };

  const getCoaTaxes = async ({ val = null, skip = 0 }) => {
    if (skip == 0) {
      setCoaTaxesLoading(true);
    }
    setCoaTaxesParams({ ...coaTaxesParams, loading: skip > 0, skip: skip });
    const payload = {
      params: {
        filter: {},
        skip: skip,
        limit: coaTaxesParams.limit,
      },
    };
    if (val) {
      payload.params.filter.cost_type = { $regex: val, $options: 'i' };
    }
    const response = await getCoaTaxSettings.request(payload);
    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(coaTaxes));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      result.unshift({ id: 'all', cost_type: 'All' });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }
      setCoaTaxes(finalResult);
      if (skip > 0) {
        setCoaTaxesParams({ ...coaTaxesParams, loading: false, skip: skip });
      }
    } else {
      if (skip <= 0) {
        setCoaTaxes([]);
      }
      setCoaTaxesParams({ ...coaTaxesParams, loading: false, skip: skip });
    }
    setCoaTaxesLoading(false);
  };

  const resetExport = () => {
    setFilterExport({
      from_date: '',
      to_date: '',
      coa_taxes: '',
      sales_team: '',
      status: '',
    });
  };

  // ── render ──
  return (
    <Layout.Container testID="skp-external-list-screen">
      {isWeb ? (
        <Layout.Title testID="skp-external-list-title" title="SKP External">
          {showExportButton ? (
            <Button
              testID="skp-external-list-export-btn"
              type="primary"
              rounded={true}
              buttonStyle={{ width: 90, marginRight: 10 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  Export
                </SociollaText>
              }
              onPress={() => {
                resetExport();
                dispatch(
                  showPopup({ name: 'showSkpExternalExport', value: true }),
                );
              }}
            />
          ) : (
            false
          )}
        </Layout.Title>
      ) : (
        false
      )}
      {isWeb ? (
        <Table
          testID="skp-external-list-table"
          filterName={scope}
          name="skp-external"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search by SKP number, Customer Name, Customer Code, Outlet Name, Outlet Code, Kwitansi Number, SKP External Account Number..."
          searchValue={searchValue}
          searchWidth={460}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => goToDetail(data)}
          slot={{
            reference_code: ({ data, index }) => (
              <View key={index}>
                <SociollaText style={{ fontSize: 14 }}>
                  {data.reference_code || '-'}
                </SociollaText>
                <ReminderNotice
                  testID={`skp-external-joined-reminder-${index}`}
                  title={data.joined_reminder}
                />
              </View>
            ),
            status: ({ data, index }) => (
              <View key={index}>
                <StatusBadge status={data.status} />
                {data.status == 'need_approval' && data.current_approval ? (
                  <SociollaText
                    style={{ fontSize: 10, marginTop: 5, textAlign: 'center' }}>
                    By: {firstLetter(deSlug(data.current_approval))}
                  </SociollaText>
                ) : (
                  false
                )}
              </View>
            ),
          }}
        />
      ) : (
        <TableMobile
          testID="skp-external-list-table-mobile"
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by SKP number, Customer Name, Customer Code, Outlet Name, Outlet Code, Kwitansi Number, SKP External Account Number"
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              testID={`skp-external-list-item-${index}`}
              activeOpacity={0.6}
              style={styles.skpItem}
              onPress={() => goToDetail(data)}
              key={index}>
              <View
                style={[styles.skpItemSection, { alignItems: 'flex-start' }]}>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 15, width: fullWidth - 140 }}>
                  {data.reference_code ? data.reference_code : '-'}
                </SociollaText>
                <View>
                  <StatusBadge status={data.status} />
                  {data.status == 'need_approval' && data.current_approval ? (
                    <SociollaText
                      style={{
                        fontSize: 10,
                        marginTop: 5,
                        textAlign: 'center',
                      }}>
                      By: {firstLetter(deSlug(data.current_approval))}
                    </SociollaText>
                  ) : (
                    false
                  )}
                </View>
              </View>
              <SociollaText
                style={{
                  marginTop: 12,
                  marginBottom: 10,
                  fontSize: 14,
                  color: Colors.grey2,
                }}>
                {data.skp_internal?.reference_code
                  ? data.skp_internal.reference_code
                  : '-'}
              </SociollaText>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Image
                  style={{ width: 16, height: 16, marginRight: 6 }}
                  width={16}
                  height={16}
                  svg={true}
                  source={require('@assets/img/icons/menu/customer.svg')}
                />
                <SociollaText type="medium" style={{ fontSize: 13 }}>
                  {data.customer?.name ? data.customer.name : '-'}
                </SociollaText>
              </View>
              {data.skp_type?.toLowerCase() == 'subdistributor' ? (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginTop: 6,
                  }}>
                  <Image
                    style={{ width: 16, height: 16, marginRight: 6 }}
                    width={16}
                    height={16}
                    svg={true}
                    source={require('@assets/img/icons/menu/catalog.svg')}
                  />
                  <SociollaText type="medium" style={{ fontSize: 13 }}>
                    {data.outlet?.name ? data.outlet.name : '-'}
                  </SociollaText>
                </View>
              ) : null}

              <View style={[styles.skpItemSection, { marginTop: 10 }]}>
                <SociollaText style={{ fontSize: 12, color: Colors.grey2 }}>
                  {data.sales_team?.name ? data.sales_team.name : '-'}
                </SociollaText>
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.blue1 }}>
                  {currency(data.total_nett, true)}
                </SociollaText>
              </View>
              <ReminderNotice
                testID={`skp-external-joined-reminder-${index}`}
                title={data.joined_reminder}
                style={{ marginTop: 12 }}
              />
            </TouchableOpacity>
          )}
        />
      )}
      {/* Filter Container */}
      <Layout.Filter
        testID="skp-external-list-filter"
        name="skp-external"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftSalesTeam('');
          setDraftCoaTaxes('');
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Sales Team
          </SociollaText>
          <SalesTeamSearch
            testID="skp-external-list-filter-sales-team"
            value={draftSalesTeam}
            placeholder="Select Sales Team"
            multiple={true}
            hideLabel
            customFilter={buildSalesTeamFilter(userData)}
            onChange={(value) => {
              setDraftSalesTeam(value);
              setDraftFilter((f) => ({ ...f, sales_team: value }));
            }}
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            COA
          </SociollaText>
          <SelectBox
            editable={true}
            testID="skp-external-list-filter-coa"
            label={'COA'}
            hideLabel
            options={coaTaxes}
            multiple={true}
            value={draftCoaTaxes}
            placeholder="Select COA"
            trackBy="id"
            labelKey="cost_type"
            formLabelKey="cost_type"
            loading={coaTaxesLoading}
            withCallback={true}
            withLoadMore
            limit={coaTaxesParams.limit}
            loadMoreEmpty={coaTaxesParams.skip > coaTaxes?.length}
            loadingCallback={coaTaxesParams.loading}
            onLoadMore={(keyword) => {
              getCoaTaxes({
                val: keyword,
                skip: coaTaxesParams.skip + coaTaxesParams.limit,
              });
            }}
            searchCallback={(keyword) => {
              getCoaTaxes({ val: keyword });
            }}
            onReady={() => {
              getCoaTaxes({});
            }}
            onChange={(value) => {
              setDraftCoaTaxes(value);
              setDraftFilter((f) => ({ ...f, coa_taxes: value }));
            }}
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Periode
          </SociollaText>
          <View style={{ width: '100%', flexDirection: 'row' }}>
            <DatePicker
              testID="skp-external-list-filter-start-date"
              label="Start Date"
              name="from_date"
              style={{
                marginRight: isWeb ? 10 : 0,
                width: isWeb ? 'auto' : '50%',
              }}
              disableAfterDate={draftFilter.to_date?.$lte}
              value={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, 'from_date')}
            />
            <DatePicker
              testID="skp-external-list-filter-end-date"
              label="End Date"
              name="to_date"
              style={{ width: isWeb ? 'auto' : '50%' }}
              value={draftFilter.to_date?.$lte}
              disableBeforeDate={draftFilter.from_date?.$gte}
              onChange={(value) => draftDateHandler(value, 'to_date')}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            testID="skp-external-list-filter-status"
            label=""
            options={statuses}
            value={draftFilter['status']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, status: val.value }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Type
          </SociollaText>
          <StatusPicker
            testID="skp-external-list-filter-type"
            label=""
            options={SKP_TYPE}
            value={draftFilter['skp_type']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, skp_type: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
      <ModalFull
        testID="skp-external-export-modal"
        title="Export SKP External"
        noTranslate={true}
        show={showSkpExternalExport}
        onClose={() => {
          resetExport();
          dispatch(showPopup({ name: 'showSkpExternalExport', value: false }));
        }}>
        <View
          style={{
            height: isWeb
              ? 'calc(100vh - 150px)'
              : dimension.height - (isAndroid ? 140 : 110),
          }}>
          <ScrollView contentContainerStyle={{ width: '100%' }}>
            <Layout.FormWrapper style={{ padding: 15, height: '100%' }}>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Sales Team
                </SociollaText>
                <SalesTeamSearch
                  testID="skp-external-export-sales-team"
                  value={filterExport.sales_team}
                  placeholder="Select Sales Team"
                  multiple={true}
                  hideLabel
                  isAll
                  customFilter={buildSalesTeamFilter(userData)}
                  onChange={(value) => {
                    setFilterExport({ ...filterExport, sales_team: value });
                  }}
                />
              </Layout.FormGroup>
              <Layout.FormGroup>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  COA
                </SociollaText>
                <SelectBox
                  testID="skp-external-export-coa"
                  label={'COA'}
                  editable={true}
                  hideLabel
                  options={coaTaxes}
                  multiple={true}
                  value={filterExport.coa_taxes}
                  placeholder="Select COA"
                  trackBy="id"
                  labelKey="cost_type"
                  formLabelKey="cost_type"
                  loading={coaTaxesLoading}
                  withCallback={true}
                  withLoadMore
                  limit={coaTaxesParams.limit}
                  loadMoreEmpty={coaTaxesParams.skip > coaTaxes?.length}
                  loadingCallback={coaTaxesParams.loading}
                  onLoadMore={(keyword) => {
                    getCoaTaxes({
                      val: keyword,
                      skip: coaTaxesParams.skip + coaTaxesParams.limit,
                    });
                  }}
                  searchCallback={(keyword) => {
                    getCoaTaxes({ val: keyword });
                  }}
                  onReady={() => {
                    getCoaTaxes({});
                  }}
                  onChange={(value) => {
                    setFilterExport({ ...filterExport, coa_taxes: value });
                  }}
                />
              </Layout.FormGroup>
              <Layout.FormGroup>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Date
                </SociollaText>
                <View
                  style={{
                    width: '100%',
                    flexDirection: isWeb ? 'row' : 'column',
                    justifyContent: 'space-between',
                  }}>
                  <DatePicker
                    testID="skp-external-export-start-date"
                    label="Start Date"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.from_date}
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        from_date: formatISO(startOfDay(value)),
                      });
                    }}
                  />
                  <DatePicker
                    testID="skp-external-export-end-date"
                    label="End Date"
                    name="created_at"
                    style={{ width: isWeb ? '49%' : '100%' }}
                    value={filterExport.to_date}
                    disableBeforeDate={
                      filterExport.from_date
                        ? filterExport.from_date
                        : addDays(new Date(), 1)
                    }
                    onChange={(value) => {
                      setFilterExport({
                        ...filterExport,
                        to_date: formatISO(endOfDay(value)),
                      });
                    }}
                  />
                </View>
              </Layout.FormGroup>
              <Layout.FormGroup>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Type
                </SociollaText>
                <StatusPicker
                  testID="skp-external-export-type"
                  label=""
                  options={SKP_TYPE_EXPORT}
                  value={filterExport.skp_type}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, skp_type: val })
                  }
                />
              </Layout.FormGroup>
              <Layout.FormGroup lastItem>
                <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
                  Status
                </SociollaText>
                <StatusPicker
                  testID="skp-external-export-status"
                  label=""
                  options={[{ name: 'All', value: 'all' }, ...statuses]}
                  value={filterExport.status}
                  trackBy="value"
                  labelKey="name"
                  minWidth={80}
                  onChange={(val) =>
                    setFilterExport({ ...filterExport, status: val.value })
                  }
                />
              </Layout.FormGroup>
            </Layout.FormWrapper>
          </ScrollView>
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              alignItems: 'flex-end',
              justifyContent: isWeb ? 'end' : 'space-between',
              paddingVertical: isWeb ? 20 : 15,
              paddingHorizontal: isWeb ? 20 : 15,
              backgroundColor: Colors.white,
              ...(!isWeb
                ? {
                    position: isMobileWeb ? 'fixed' : 'absolute',
                    bottom: 0,
                    right: 0,
                    left: 0,
                  }
                : {}),
            }}>
            <Button
              testID="skp-external-export-cancel-btn"
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
                backgroundColor: Colors.white,
                marginRight: 10,
              }}
              title={
                <SociollaText type="medium" style={{ fontSize: 14 }}>
                  Cancel
                </SociollaText>
              }
              onPress={() => {
                resetExport();
                dispatch(
                  showPopup({ name: 'showSkpExternalExport', value: false }),
                );
              }}
            />
            <Button
              testID="skp-external-export-submit-btn"
              rounded={true}
              type="primary"
              buttonStyle={{
                width: isWeb
                  ? 100
                  : dimension.width / 2 - (isMobileWeb ? 20 : 25),
              }}
              disabledStyle={{ width: 90 }}
              disabled={exporting}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  {exporting ? 'Loading...' : 'Export'}
                </SociollaText>
              }
              onPress={() =>
                onExport().then(() => {
                  resetExport();
                  dispatch(
                    showPopup({ name: 'showSkpExternalExport', value: false }),
                  );
                })
              }
            />
          </View>
        </View>
      </ModalFull>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  skpItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  skpItemSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: fullWidth - 20,
  },
});

export default SkpExternalScreen;
